import { a as __rest } from '../../tslib.es6.esm.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useId, useState, useRef, useEffect } from 'react';
import { ExclamationCircleIcon } from '@iris/react-icons/ExclamationCircleIcon';
import { INPUT_VARIANT } from '../../utils/constant.esm.js';
import { BaseInput } from '../BaseInput/BaseInput.esm.js';
import { BaseLabel } from '../BaseLabel/BaseLabel.esm.js';
import { InlineMessage } from '../InlineMessage/InlineMessage.esm.js';
import inputStyles from './TextInput.styles.esm.js';
import { cn } from '../../utils/cn.esm.js';

const TextInput = forwardRef((props, ref) => {
    const generatedId = useId();
    const { id = generatedId, label, error, errorMessage, helperText, helperTextMessage, disabled, className, inverse = false, variant = INPUT_VARIANT.Outer, placeholder, value, helperTextAriaLabel, onChange, onFocus, onBlur } = props, otherProps = __rest(props, ["id", "label", "error", "errorMessage", "helperText", "helperTextMessage", "disabled", "className", "inverse", "variant", "placeholder", "value", "helperTextAriaLabel", "onChange", "onFocus", "onBlur"]);
    const { input: inputClasses, labelOuter: labelOuterClasses, labelInner: labelInnerClasses, label: labelClasses, errorMessage: errorClasses, helperText: helperTextClasses, labelInputWrapper: labelInputWrapperClasses, wrapper: wrapperClasses, } = inputStyles({
        inverse,
        variant,
        error,
        disabled,
    });
    // Determine if this is a controlled or uncontrolled input
    const isControlled = value !== undefined;
    // For uncontrolled inputs, use internal state
    const [internalValue, setInternalValue] = useState("");
    // Use controlled value or internal value
    const textValue = isControlled ? value || "" : internalValue;
    const hasValue = !!textValue && textValue.length > 0;
    const isEmpty = !hasValue;
    const [accessibilityKey, setAccessibilityKey] = useState(0); // Key to force re-render when value changes
    const wasEmpty = useRef(true); // Previous empty state tracking
    const [isFocused, setIsFocused] = useState(false);
    useEffect(() => {
        if (isEmpty) {
            // Save empty state to ref
            wasEmpty.current = true;
        }
        else if (hasValue && wasEmpty.current) {
            // Only set accessibility key when changing from empty to has value
            setAccessibilityKey((prev) => prev + 1);
            wasEmpty.current = false; // Mark as no longer empty
        }
        // If hasValue but was not empty before, do nothing
    }, [hasValue, isEmpty]);
    const handleChange = (e) => {
        // Update internal state for uncontrolled inputs
        if (!isControlled) {
            setInternalValue(e.target.value);
        }
        // Always call the onChange callback if provided
        if (onChange) {
            onChange(e);
        }
    };
    const ariaDescribedBy = [
        isEmpty && label && `${id}-label`,
        !textValue && placeholder && `${id}-placeholder`,
        errorMessage && `${id}-error`,
        helperTextMessage && `${id}-helper-text`,
    ]
        .filter(Boolean)
        .join(" ") || undefined;
    const handleFocus = (e) => {
        onFocus && onFocus(e);
        setIsFocused(true);
    };
    const handleBlur = (e) => {
        onBlur && onBlur(e);
        setIsFocused(false);
    };
    const placeholderForInput = variant === INPUT_VARIANT.Outer
        ? placeholder
        : variant === INPUT_VARIANT.Inner && isFocused && !textValue
            ? placeholder
            : "";
    return (jsxs("div", { className: wrapperClasses(), children: [jsxs("div", { className: labelInputWrapperClasses(), children: [jsx(BaseInput, Object.assign({ id: id, inverse: inverse, className: cn(inputClasses({ class: className })), disabled: disabled, placeholder: placeholderForInput, ref: ref, onFocus: handleFocus, onBlur: handleBlur, onChange: handleChange, value: textValue, "aria-labelledby": `${id}-label`, "aria-describedby": ariaDescribedBy }, otherProps)), label && variant === INPUT_VARIANT.Outer && (jsx(BaseLabel, { id: `${id}-label`, htmlFor: id, inverse: inverse, disabled: disabled, "aria-hidden": isEmpty ? true : false, className: cn(labelClasses(), labelOuterClasses()), children: label }, `label-outer-${generatedId}-${accessibilityKey}`)), label && variant === INPUT_VARIANT.Inner && (jsx(BaseLabel, { id: `${id}-label`, htmlFor: id, inverse: inverse, disabled: disabled, "aria-hidden": isEmpty ? true : false, className: cn(labelClasses(), labelInnerClasses()), children: label }, `label-inner-${generatedId}-${accessibilityKey}`))] }), !textValue && placeholder && (jsx("span", { id: `${id}-placeholder`, className: "iris:sr-only", "aria-hidden": "true", children: placeholder })), error && errorMessage && (jsx(InlineMessage, { id: id, disabled: disabled, icon: jsx(ExclamationCircleIcon, {}), iconClass: errorClasses(), label: errorMessage, "aria-hidden": isEmpty ? true : false, labelClass: errorClasses() })), helperText && (jsxs("div", { className: "iris:flex", children: [jsx("span", { id: `${id}-helper-text`, className: helperTextClasses(), "aria-hidden": true, children: helperTextMessage }), jsx("span", { className: "iris:sr-only", children: helperTextAriaLabel })] }))] }));
});

export { TextInput };
